/**
 * Icon Resolver with ontology inheritance support.
 *
 * Resolves icons for REC/WOBO types by:
 * 1. Checking for a direct match in the registry
 * 2. Walking up the superclass chain via the Ontology service
 * 3. Falling back to a default icon
 *
 * Icons are returned as strings - either MDI (mdi-*) or WoB custom ($wob-*).
 * WoB icons are registered in Vuetify's icon system (src/plugins/vuetify.js).
 */

import { iconRegistry, alternativeIconRegistry } from './icon-registry'

const DEFAULT_ICON = { mdi: 'mdi-asterisk' }

/**
 * Normalize a type to its lowercase short name.
 * Handles: IRI strings, prefixed strings (rec:Room), or objects with .short property.
 */
function normalizeType(type) {
  if (!type) return ''
  if (typeof type === 'object' && type.short) return type.short.toLowerCase()
  if (typeof type !== 'string') return ''

  const lastSep = Math.max(
    type.lastIndexOf(':'),
    type.lastIndexOf('#'),
    type.lastIndexOf('/')
  )
  return (lastSep >= 0 ? type.substring(lastSep + 1) : type).toLowerCase()
}

/**
 * Get the prefixed form of a type for namespace-aware lookups.
 * Returns lowercase prefixed form like "rec:point" or "brick:point".
 * Handles: objects with .prefixed property, or prefixed strings.
 */
function getPrefixedType(type) {
  if (!type) return null
  if (typeof type === 'object' && type.prefixed)
    return type.prefixed.toLowerCase()
  if (typeof type === 'string') {
    // If it's already prefixed (contains : but not :// or #)
    if (type.includes(':') && !type.includes('://') && !type.includes('#')) {
      return type.toLowerCase()
    }
  }
  return null
}

/**
 * Resolve an icon for a given type, optionally walking the ontology hierarchy.
 *
 * @param {string|object} type - Type IRI, short name, or object with .short
 * @param {object} [options]
 * @param {object} [options.ontology] - Ontology instance (with superclasses() method)
 * @param {boolean} [options.alternative] - Use alternative icon if available
 * @returns {{ mdi: string }} Icon descriptor with mdi property
 */
export function resolveIcon(type, options = {}) {
  const { ontology, alternative = false } = options
  const typeName = normalizeType(type)
  if (!typeName) return DEFAULT_ICON

  // Get prefixed form for namespace-aware lookups (e.g., "rec:point" vs "brick:point")
  const prefixedName = getPrefixedType(type)

  // Alternative icons (alarm active states etc.)
  // Check prefixed first, then short name
  if (alternative) {
    if (prefixedName && alternativeIconRegistry[prefixedName]) {
      return alternativeIconRegistry[prefixedName]
    }
    if (alternativeIconRegistry[typeName]) {
      return alternativeIconRegistry[typeName]
    }
  }

  // Direct match: check prefixed form first, then short name
  if (prefixedName && iconRegistry[prefixedName])
    return { mdi: iconRegistry[prefixedName] }
  if (iconRegistry[typeName]) return { mdi: iconRegistry[typeName] }

  // Walk superclass chain if ontology is available
  if (ontology && typeof ontology.superclasses === 'function') {
    try {
      // The ontology expects an Iri, but we may have a string.
      // If it's already an Iri-like object, pass directly; otherwise try ontology.iri()
      let typeIri = type
      if (typeof type === 'string' && typeof ontology.iri === 'function') {
        try {
          typeIri = ontology.iri(type)
        } catch (e) {
          // If IRI creation fails, we can't do hierarchy lookup
          return DEFAULT_ICON
        }
      }

      const superclasses = ontology.superclasses(typeIri)
      if (Array.isArray(superclasses)) {
        for (const sc of superclasses) {
          const scName = normalizeType(sc)
          if (iconRegistry[scName]) return { mdi: iconRegistry[scName] }
        }
      }
    } catch (e) {
      // Hierarchy lookup failed, fall through to default
    }
  }

  return DEFAULT_ICON
}

/**
 * Resolve to an icon string (MDI or $wob-*).
 */
export function resolveIconMdi(type, options = {}) {
  const icon = resolveIcon(type, options)
  return icon.mdi || DEFAULT_ICON.mdi
}

export { DEFAULT_ICON, normalizeType as _normalizeType }
